import { useCallback, useEffect, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { WASTE } from "@/constants/testIds";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { GapBadge } from "@/components/waste/GapBadge";
import { Plus, Pencil, KeyRound, BarChart3, Truck } from "lucide-react";
import { toast } from "sonner";

const BLANK = { name: "", licence_no: "", services: "", contact_email: "", contract_ids: [] };

function SubDialog({ value, setValue, projects, onSave }) {
  const f = (k) => (e) => setValue((v) => ({ ...v, [k]: e.target.value }));
  return (
    <Dialog open={!!value} onOpenChange={(o) => !o && setValue(null)}>
      <DialogContent className="max-h-[88vh] overflow-y-auto sm:max-w-lg">
        <DialogHeader><DialogTitle className="font-heading">{value?.id ? "Edit" : "Add"} subcontractor</DialogTitle></DialogHeader>
        {value && <div className="space-y-3">
          <div className="space-y-1.5"><Label>Company name</Label><Input value={value.name} onChange={f("name")} data-testid={WASTE.subField("name")} /></div>
          <div className="grid grid-cols-2 gap-2">
            <div className="space-y-1.5"><Label>Licence number</Label><Input value={value.licence_no || ""} onChange={f("licence_no")} data-testid={WASTE.subField("licence_no")} /></div>
            <div className="space-y-1.5"><Label>Contact email</Label><Input value={value.contact_email || ""} onChange={f("contact_email")} data-testid={WASTE.subField("contact_email")} /></div>
          </div>
          <div className="space-y-1.5"><Label>Services (comma separated)</Label><Input value={value.services} onChange={f("services")} data-testid={WASTE.subField("services")} /></div>
          <div className="space-y-1.5"><Label>Assigned projects</Label>
            {projects.map((p) => (
              <label key={p.contract_id} className="flex items-center gap-2 text-sm">
                <Checkbox checked={value.contract_ids.includes(p.contract_id)} data-testid={WASTE.subProject(p.contract_id)}
                  onCheckedChange={(c) => setValue((v) => ({ ...v, contract_ids: c ? [...v.contract_ids, p.contract_id] : v.contract_ids.filter((x) => x !== p.contract_id) }))} />
                {p.name}
              </label>
            ))}
          </div>
        </div>}
        <DialogFooter><Button variant="outline" onClick={() => setValue(null)}>Cancel</Button><Button disabled={!value?.name} onClick={onSave} className="bg-forest-mid hover:bg-forest-dark" data-testid={WASTE.subSave}>Save</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function LoginDialog({ sub, onClose, onDone }) {
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const save = async () => {
    try { await api.post(`/subcontractors/${sub.id}/login`, form); toast.success("Login created"); onDone(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  return (
    <Dialog open={!!sub} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader><DialogTitle className="font-heading">Create login — {sub?.name}</DialogTitle></DialogHeader>
        <p className="text-xs text-muted-foreground">This login can only see and report on the projects assigned to this company.</p>
        {["name", "email", "password"].map((k) => (
          <div key={k} className="space-y-1.5"><Label className="capitalize">{k === "password" ? "Initial password (min 8 characters)" : k}</Label>
            <Input type={k === "password" ? "password" : "text"} value={form[k]} onChange={(e) => setForm((f) => ({ ...f, [k]: e.target.value }))} data-testid={WASTE.loginField(k)} /></div>
        ))}
        <DialogFooter><Button variant="outline" onClick={onClose}>Cancel</Button><Button onClick={save} className="bg-forest-mid hover:bg-forest-dark" data-testid={WASTE.loginSave}>Create login</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Scorecard({ subId, onClose }) {
  const [d, setD] = useState(null);
  useEffect(() => { if (subId) api.get(`/subcontractors/${subId}/scorecard`).then((r) => setD(r.data)); else setD(null); }, [subId]);
  return (
    <Dialog open={!!subId} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[88vh] overflow-y-auto sm:max-w-2xl" data-testid={WASTE.scorecard}>
        <DialogHeader><DialogTitle className="font-heading">Scorecard — {d?.subcontractor?.name}</DialogTitle></DialogHeader>
        {d && <div className="space-y-4">
          <div className="flex items-center gap-2"><span className="text-sm text-muted-foreground">Latest status across projects</span><GapBadge status={d.status} testid={WASTE.scoreStat("status")} /></div>
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
            {[["avg_diversion_pct", "Avg diversion", "%"], ["evidence_completeness_pct", "Evidence complete", "%"], ["flagged_lines", "Flagged lines", ""], ["report_count", "Reports", ""]].map(([k, l, u]) => (
              <div key={k} className="rounded-xl border border-sage-light bg-sage-surface p-3 text-center" data-testid={WASTE.scoreStat(k)}>
                <p className="font-heading text-xl font-bold text-forest-dark">{d[k]}{u}</p><p className="text-[10px] text-muted-foreground">{l}</p></div>
            ))}
          </div>
          <div><p className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Gap trend</p>
            <div className="space-y-1.5">{d.trend.map((t) => (
              <div key={t.report_id} className="flex items-center gap-2 text-xs">
                <span className="w-16 shrink-0 text-muted-foreground">{t.period}</span>
                <div className="h-2 flex-1 overflow-hidden rounded-full bg-sage-surface"><div className="h-full rounded-full bg-forest-mid" style={{ width: `${t.diversion_pct}%` }} /></div>
                <span className="w-12 text-right">{t.diversion_pct}%</span><GapBadge status={t.status} />
                <span className="hidden w-40 truncate text-muted-foreground sm:inline">{t.project}</span>
              </div>))}
              {d.trend.length === 0 && <p className="text-xs text-muted-foreground">No submitted reports yet.</p>}
            </div></div>
        </div>}
      </DialogContent>
    </Dialog>
  );
}

export function SubcontractorsTab({ projects }) {
  const { hasRole } = useAuth();
  const [subs, setSubs] = useState([]);
  const [edit, setEdit] = useState(null);
  const [login, setLogin] = useState(null);
  const [score, setScore] = useState(null);
  const load = useCallback(() => api.get("/subcontractors").then((r) => setSubs(r.data)).catch((e) => toast.error(apiErr(e))), []);
  useEffect(() => { load(); }, [load]);
  const save = async () => {
    const body = { ...edit, services: edit.services.split(",").map((s) => s.trim()).filter(Boolean) };
    try { edit.id ? await api.patch(`/subcontractors/${edit.id}`, body) : await api.post("/subcontractors", body); setEdit(null); load(); toast.success("Saved"); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const pname = (id) => projects.find((p) => p.contract_id === id)?.name || "Project";
  return (
    <div className="space-y-3">
      <div className="flex justify-end"><Button size="sm" className="bg-forest-mid hover:bg-forest-dark" onClick={() => setEdit({ ...BLANK })} data-testid={WASTE.subAddBtn}><Plus className="mr-1 h-4 w-4" /> Add subcontractor</Button></div>
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        {subs.map((s) => (
          <Card key={s.id} className="border-sage-light p-4" data-testid={WASTE.subCard(s.id)}>
            <div className="flex items-start gap-3"><Truck className="mt-0.5 h-5 w-5 text-forest-mid" />
              <div className="min-w-0 flex-1">
                <p className="font-semibold text-forest-dark">{s.name}</p>
                <p className="text-xs text-muted-foreground">Licence {s.licence_no || "—"} · {(s.services || []).join(", ") || "No services listed"}</p>
                <p className="mt-1 text-xs text-forest-dark">{(s.contract_ids || []).map(pname).join(" · ") || "Not assigned to any project"}</p>
                <p className="mt-1 text-[11px] text-muted-foreground">Logins: {s.logins.map((l) => l.email).join(", ") || "none"}</p>
              </div></div>
            <div className="mt-3 flex flex-wrap gap-2">
              <Button size="sm" variant="outline" onClick={() => setScore(s.id)} data-testid={WASTE.subScore(s.id)}><BarChart3 className="mr-1 h-3.5 w-3.5" /> Scorecard</Button>
              <Button size="sm" variant="ghost" onClick={() => setEdit({ ...s, services: (s.services || []).join(", "), contract_ids: s.contract_ids || [] })} data-testid={WASTE.subEdit(s.id)}><Pencil className="mr-1 h-3.5 w-3.5" /> Edit</Button>
              {hasRole("tenant_admin") && <Button size="sm" variant="ghost" onClick={() => setLogin(s)} data-testid={WASTE.subLogin(s.id)}><KeyRound className="mr-1 h-3.5 w-3.5" /> Create login</Button>}
            </div>
          </Card>
        ))}
      </div>
      <SubDialog value={edit} setValue={setEdit} projects={projects} onSave={save} />
      {login && <LoginDialog sub={login} onClose={() => setLogin(null)} onDone={() => { setLogin(null); load(); }} />}
      <Scorecard subId={score} onClose={() => setScore(null)} />
    </div>
  );
}
